import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Heart, MessageCircle, Share2, Tag, Clock, ExternalLink, Pin,
  MoreVertical, Trash2, EyeOff, Loader2,
} from 'lucide-react';
import { api, timeAgo, money, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { ARENA } from '../lib/classTheme';
import { Avatar, Points } from './ui';

export default function PostCard({ post, onOpen, onDeleted }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [liked, setLiked] = useState(!!post.liked);
  const [likes, setLikes] = useState(post.likes_count);
  const [shared, setShared] = useState(!!post.shared);
  const [menu, setMenu] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const canDelete = post.author_id === user?.id || user?.role === 'admin';

  const likeM = useMutation({
    mutationFn: () => api.post(`/posts/${post.id}/like`),
    onMutate: () => { setLiked(v => !v); setLikes(v => v + (liked ? -1 : 1)); },
    onError: () => { setLiked(v => !v); setLikes(v => v + (liked ? 1 : -1)); },
  });

  const shareM = useMutation({
    mutationFn: () => api.post(`/posts/${post.id}/share`, { channel: 'link' }),
    onSuccess: async ({ data }) => {
      setShared(true);
      const text = `${post.title || post.body?.slice(0, 80)} — Wall of Freedom`;
      if (navigator.share) {
        try { await navigator.share({ title: 'Wall of Freedom', text, url: data.shareUrl }); } catch {}
      } else {
        await navigator.clipboard.writeText(data.shareUrl);
      }
      if (data.awarded) toast.success(`+${data.awarded} points for sharing! 💎`);
      else if (data.capped) toast(`Daily share limit reached`, { icon: '⏳' });
      else toast.success('Link copied!');
      useAuth.getState().load();
      qc.invalidateQueries(['feed']);
    },
    onError: e => toast.error(e.message),
  });

  const deleteM = useMutation({
    mutationFn: () => api.delete(`/posts/${post.id}`),
    onSuccess: () => {
      setDeleted(true);
      toast.success('Post removed');
      setTimeout(() => {
        qc.invalidateQueries(['feed']);
        onDeleted?.(post.id);
      }, 320);
    },
    onError: e => toast.error(e.message),
  });

  const hideM = useMutation({
    mutationFn: () => api.patch(`/posts/${post.id}`, { status: 'hidden' }),
    onSuccess: () => { setDeleted(true); toast('Post hidden'); qc.invalidateQueries(['feed']); },
    onError: e => toast.error(e.message),
  });

  const isOffer = post.type === 'offer';
  const expired = post.valid_until && new Date(post.valid_until) < new Date();

  return (
    <AnimatePresence>
      {!deleted && (
        <motion.article
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, height: 0, marginBottom: 0, scale: .95 }}
          transition={{ duration: .3 }}
          className="card overflow-hidden"
          style={post.pinned ? { borderColor: ARENA.gold, borderWidth: 2 } : {}}>

          {post.pinned && (
            <div className="px-4 py-1.5 flex items-center gap-1.5 text-[10px] font-display font-black"
              style={{ background: 'rgba(201,151,58,.12)', color: ARENA.goldD }}>
              <Pin size={11} /> PINNED
            </div>
          )}

          <div className="p-4">
            <div className="flex items-start gap-3">
              <Link to={post.shop_slug ? `/shop/${post.shop_slug}` : `/u/${post.username}`}>
                <Avatar src={post.shop_logo || post.avatar_url}
                  name={post.shop_name || post.username} size={42}
                  square={!!post.shop_slug} gold />
              </Link>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link to={post.shop_slug ? `/shop/${post.shop_slug}` : `/u/${post.username}`}
                    className="font-display font-bold text-sm hover:underline truncate">
                    {post.shop_name || post.display_name || post.username}
                  </Link>
                  {post.shop_slug && <span className="chip-mint">MERCHANT</span>}
                  {isOffer && <span className="chip-gold">🔥 OFFER</span>}
                  {post.type === 'announcement' && <span className="chip-grape">📢 NEWS</span>}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold"
                  style={{ color: 'var(--stone-400)' }}>
                  <span>{timeAgo(post.created_at)}</span>
                  {post.group_handle && (
                    <>
                      <span>·</span>
                      <Link to={`/g/${encodeURIComponent(post.group_handle)}`}
                        className="hover:underline" style={{ color: ARENA.goldD }}>
                        {post.group_handle}
                      </Link>
                    </>
                  )}
                </div>
              </div>

              {canDelete && (
                <div className="relative shrink-0">
                  <button onClick={() => setMenu(v => !v)}
                    className="p-1.5 rounded-lg transition hover:bg-stone-100"
                    style={{ color: 'var(--stone-400)' }}>
                    <MoreVertical size={16} />
                  </button>
                  {menu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
                      <motion.div initial={{ opacity: 0, scale: .9, y: -6 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="absolute right-0 top-9 z-50 w-44 card p-1.5">
                        {user?.role === 'admin' && post.author_id !== user.id && (
                          <button onClick={() => { setMenu(false); hideM.mutate(); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm
                              font-semibold transition hover:bg-stone-100"
                            style={{ color: 'var(--stone-600)' }}>
                            <EyeOff size={15} /> Hide post
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setMenu(false);
                            if (confirm('Delete this post permanently?')) deleteM.mutate();
                          }}
                          disabled={deleteM.isPending}
                          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm
                            font-semibold transition hover:bg-blood-500/10"
                          style={{ color: ARENA.blood }}>
                          {deleteM.isPending
                            ? <Loader2 size={15} className="animate-spin" />
                            : <Trash2 size={15} />}
                          Delete post
                        </button>
                      </motion.div>
                    </>
                  )}
                </div>
              )}
            </div>

            {post.title && (
              <h3 className="font-display font-extrabold text-base mt-3">{post.title}</h3>
            )}
            {post.body && (
              <p className="text-sm mt-1.5 whitespace-pre-wrap leading-relaxed"
                style={{ color: 'var(--stone-700)' }}>{post.body}</p>
            )}

            {isOffer && post.price && (
              <div className="mt-3 flex items-center gap-3 p-3 rounded-xl"
                style={expired
                  ? { background: 'var(--stone-100)' }
                  : {
                      background: 'linear-gradient(135deg, rgba(201,151,58,.10), rgba(232,200,116,.06))',
                      border: `1px solid ${ARENA.gold}44`,
                    }}>
                <Tag size={20} style={{ color: expired ? 'var(--stone-400)' : ARENA.goldD }} />
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-xl font-black"
                      style={{
                        color: expired ? 'var(--stone-400)' : ARENA.goldD,
                        textDecoration: expired ? 'line-through' : 'none',
                      }}>
                      {money(post.price, post.currency)}
                    </span>
                    {post.old_price && (
                      <span className="text-sm line-through" style={{ color: 'var(--stone-400)' }}>
                        {money(post.old_price, post.currency)}
                      </span>
                    )}
                    {post.discount_pct > 0 && (
                      <span className="chip text-white" style={{ background: ARENA.blood }}>
                        −{post.discount_pct}%
                      </span>
                    )}
                  </div>
                  {post.valid_until && (
                    <p className="text-[11px] font-bold flex items-center gap-1 mt-0.5"
                      style={{ color: expired ? 'var(--stone-400)' : ARENA.goldD }}>
                      <Clock size={10} />
                      {expired ? 'Expired' : `Until ${new Date(post.valid_until).toLocaleDateString()}`}
                    </p>
                  )}
                </div>
                {post.price && !expired && (
                  <div className="text-right shrink-0">
                    <p className="text-[9px] font-display font-black"
                      style={{ color: 'var(--stone-400)' }}>YOU EARN</p>
                    <Points value={Math.floor(post.price)} size="sm" />
                  </div>
                )}
              </div>
            )}
          </div>

          {post.image_url && (
            <button onClick={() => onOpen?.(post)} className="block w-full">
              <img src={post.image_url} alt="" className="w-full max-h-[420px] object-cover" loading="lazy" />
            </button>
          )}

          {post.link_url && (
            <a href={post.link_url} target="_blank" rel="noreferrer"
              className="mx-4 my-3 flex items-center gap-2 p-2.5 rounded-xl text-sm font-bold transition"
              style={{ background: 'var(--sand-100)', color: 'var(--stone-600)' }}>
              <ExternalLink size={14} />
              <span className="truncate">{post.link_url.replace(/^https?:\/\//, '')}</span>
            </a>
          )}

          <div className="px-2 py-1.5 flex items-center"
            style={{ borderTop: '1px solid var(--stone-200)' }}>
            <Btn active={liked} onClick={() => likeM.mutate()} icon={Heart}
              label={compact(likes)} color={ARENA.blood} fill={liked} />
            <Link to={`/post/${post.id}`} className="flex-1">
              <Btn icon={MessageCircle} label={compact(post.comments_count)} />
            </Link>
            <Btn active={shared} onClick={() => shareM.mutate()} icon={Share2}
              label={shared ? 'Shared' : 'Share +5💎'} color="#047857" />
          </div>
        </motion.article>
      )}
    </AnimatePresence>
  );
}

function Btn({ icon: Icon, label, onClick, active, color, fill }) {
  return (
    <button onClick={onClick}
      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs
        font-bold transition active:scale-95 hover:bg-stone-100"
      style={{ color: active ? (color || ARENA.goldD) : 'var(--stone-500)' }}>
      <Icon size={17} className={fill ? 'fill-current' : ''} /> {label}
    </button>
  );
}